import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { CubeQuery, CubeQueryRecord, RequestTiming } from '../types/cubeQueryTypes';
import { extractComponentNameWithFallback } from './componentNameExtractor';

interface InterceptorConfig extends InternalAxiosRequestConfig {
  __requestId?: string;
}

interface InterceptorCallbacks {
  onQueryCaptured: (record: CubeQueryRecord) => void;
  getIsRecording: () => boolean;
  getMaxQueries: () => number;
  getCurrentQueriesCount: () => number;
  generateRequestId: () => string;
  extractQueryKey: (cubeQuery: CubeQuery) => string[] | undefined;
}

let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;
const requestTimings = new Map<string, RequestTiming>();

const isCubeRequest = (url: string): boolean => {
  return (
    (url.includes('cube.customer.roche.com') || url.includes('cubejs-api')) &&
    (url.includes('/v1/load') || url.includes('/cubejs-api/v1'))
  );
};

const parseQueryFromUrl = (fullUrl: string): CubeQuery | null => {
  try {
    const parsedUrl = new URL(fullUrl, window.location.origin);
    const queryParam = parsedUrl.searchParams.get('query');
    if (queryParam) {
      return JSON.parse(decodeURIComponent(queryParam));
    }
  } catch {}
  return null;
};

export const setupAxiosInterceptor = (callbacks: InterceptorCallbacks): void => {
  if (requestInterceptorId !== null) {
    return;
  }

  requestInterceptorId = axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (!callbacks.getIsRecording()) return config;

      const url = config.url || '';
      const baseURL = config.baseURL || '';
      const fullUrl = baseURL ? `${baseURL}${url}` : url;

      if (isCubeRequest(fullUrl)) {
        const requestId = callbacks.generateRequestId();
        const query = parseQueryFromUrl(fullUrl);
        const queryKey = query ? callbacks.extractQueryKey(query) : undefined;
        const componentName = extractComponentNameWithFallback(query ?? undefined, queryKey);

        requestTimings.set(requestId, {
          startTime: Date.now(),
          componentName,
          query,
          queryKey,
        });

        (config as InterceptorConfig).__requestId = requestId;
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  responseInterceptorId = axios.interceptors.response.use(
    (response: AxiosResponse) => {
      const requestId = (response.config as InterceptorConfig).__requestId;
      if (requestId && requestTimings.has(requestId)) {
        const timing = requestTimings.get(requestId)!;
        const duration = Date.now() - timing.startTime;
        const baseURL = response.config.baseURL || '';
        const url = response.config.url || '';
        const fullUrl = baseURL ? `${baseURL}${url}` : url;

        if (callbacks.getCurrentQueriesCount() < callbacks.getMaxQueries()) {
          const record: CubeQueryRecord = {
            id: requestId,
            timestamp: timing.startTime,
            componentName: timing.componentName,
            pagePath: typeof window !== 'undefined' ? window.location.pathname : '/',
            query: timing.query || { measures: [], dimensions: [] },
            queryKey: timing.queryKey || [],
            url: fullUrl,
            method: (response.config.method?.toUpperCase() as 'GET' | 'POST') || 'GET',
            duration,
            status: response.status,
          };

          callbacks.onQueryCaptured(record);
        }

        requestTimings.delete(requestId);
      }
      return response;
    },
    (error: AxiosError) => {
      const config = error.config as InterceptorConfig | undefined;
      const requestId = config?.__requestId;

      if (requestId && requestTimings.has(requestId)) {
        const timing = requestTimings.get(requestId)!;
        const duration = Date.now() - timing.startTime;

        if (callbacks.getCurrentQueriesCount() < callbacks.getMaxQueries()) {
          const record: CubeQueryRecord = {
            id: requestId,
            timestamp: timing.startTime,
            componentName: timing.componentName,
            pagePath: typeof window !== 'undefined' ? window.location.pathname : '/',
            query: timing.query || { measures: [], dimensions: [] },
            queryKey: timing.queryKey || [],
            url: config?.url || '',
            method: (config?.method?.toUpperCase() as 'GET' | 'POST') || 'GET',
            duration,
            status: error.response?.status,
            error: error.message || 'Request failed',
          };

          callbacks.onQueryCaptured(record);
        }

        requestTimings.delete(requestId);
      }
      return Promise.reject(error);
    },
  );
};

export const removeAxiosInterceptor = (): void => {
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }
  requestTimings.clear();
};

export const clearRequestTimings = (): void => {
  requestTimings.clear();
};
